import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { auth, db } from '../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import SeasonFilter from './SeasonFilter';
import { loadPlayerSeasonEntriesOnce } from '../utils/playerStatsSessionCache';
import { buildDashboardAnalytics } from './MyStats/utils/dashboardAnalytics';
import { TeamFormCard, ModeHealth, MapPoolHealth, PlayerForm, CoachingFlags, RecentResults } from './MyStats/dashboard/DashboardSections';
import DashboardEvidenceDrawer from './MyStats/dashboard/DashboardEvidenceDrawer';
import './MyStats/dashboard/TeamDashboardV2.css';
import { getCurrentSeason, SEASONS } from '../data/seasons';
import { listSeasons } from '../utils/seasonApi';
import {
  MATCH_TYPE_CATEGORIES,
  getMatchTypeOptions,
  getMatchTypeRegions,
  getSeasonConfig,
} from '../utils/matchTypeCatalog';

const normalizeFilterValue = (value) => String(value || '').trim().toUpperCase();

const DashboardOverview = ({ onViewPlayer }) => {
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const evidenceTrigger = useRef(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [region, setRegion] = useState('');
  const [enemyTeam, setEnemyTeam] = useState('');
  const [selectedMatchCategory, setSelectedMatchCategory] = useState('');
  const [selectedMatchRegion, setSelectedMatchRegion] = useState('');
  const [selectedMatchTypes, setSelectedMatchTypes] = useState([]);
  const [matchTypeDropdownOpen, setMatchTypeDropdownOpen] = useState(false);
  const [vsTeams, setVsTeams] = useState([]);
  const [matchTypes, setMatchTypes] = useState([]);
  const [matchTypeCatalog, setMatchTypeCatalog] = useState([]);
  const [authUid, setAuthUid] = useState(null);
  const [teamId, setTeamId] = useState(null);
  const [teamLogo, setTeamLogo] = useState(null);
  const [allStats, setAllStats] = useState([]);
  const [playerEntries, setPlayerEntries] = useState([]);
  const [activePlayers, setActivePlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  const [selectedSeason, setSelectedSeason] = useState(getCurrentSeason());
  const [seasonDocId, setSeasonDocId] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [seasonTeamName, setSeasonTeamName] = useState('');
  const [seasonTeamLogo, setSeasonTeamLogo] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setAuthUid(null);
        setTeamId(null);
        setTeamLogo(null);
        setSeasons([]);
        setAllStats([]);
        setPlayerEntries([]);
        setMatchTypeCatalog([]);
        return;
      }
      setAuthUid(user.uid);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!authUid) return;
    let active = true;

    (async () => {
      const userRef = doc(db, `users/${authUid}`);
      const userSnap = await getDoc(userRef);
      if (!active || !userSnap.exists()) return;
      const userData = userSnap.data() || {};
      setTeamId(userData.teamId || null);
    })().catch((err) => {
      console.error('Failed to resolve dashboard team', err);
    });

    return () => {
      active = false;
    };
  }, [authUid]);

  useEffect(() => {
    if (!teamId) return;
    const currentSeason = getCurrentSeason();
    setSelectedSeason(currentSeason);
  }, [teamId]);

  useEffect(() => {
    if (!teamId) return;
    let active = true;

    (async () => {
      const [seasonRows, teamSnap] = await Promise.all([
        listSeasons(teamId),
        getDoc(doc(db, `teams/${teamId}`)),
      ]);

      if (!active) return;
      setSeasons(seasonRows);
      setTeamLogo(teamSnap.exists() ? (teamSnap.data()?.logo || null) : null);
    })().catch((err) => {
      console.error('Failed to load dashboard metadata', err);
    });

    return () => {
      active = false;
    };
  }, [teamId]);

  useEffect(() => {
    const row =
      seasons.find((s) => String(s.year || s.id) === String(selectedSeason)) ||
      seasons.find((s) => String(s.id) === String(selectedSeason)) ||
      seasons[0];
    setSeasonDocId(row ? String(row.id) : null);
    setSelectedMatchCategory('');
    setSelectedMatchRegion('');
    setSelectedMatchTypes([]);
    setMatchTypeDropdownOpen(false);
  }, [selectedSeason, seasons]);

  useEffect(() => {
    if (!teamId) return;
    let active = true;

    (async () => {
      const configSnap = await getDoc(doc(db, `teams/${teamId}/configurations/stats`));
      if (!active) return;

      if (!configSnap.exists()) {
        setVsTeams([]);
        setMatchTypes([]);
        setMatchTypeCatalog([]);
        setSeasonTeamName('');
        setSeasonTeamLogo(teamLogo || '');
        setActivePlayers([]);
        return;
      }

      const config = configSnap.data();
      const seasonConfig = getSeasonConfig(config, selectedSeason);

      const normalizedTeams = (seasonConfig.vsTeams || []).map((t) => ({
        name: t.name.trim().toUpperCase(),
        region: t.region.trim().toUpperCase()
      }));
      setVsTeams(normalizedTeams);
      setMatchTypes(seasonConfig.matchTypes || []);
      setMatchTypeCatalog(seasonConfig.matchTypeCatalog || []);
      setSeasonTeamName(seasonConfig.teamName || '');
      setSeasonTeamLogo(seasonConfig.teamLogo || teamLogo || '');
      setActivePlayers((config.players || []).filter((player) => player.isActive !== false)
        .map((player) => player.ign || player.name).filter(Boolean));
    })().catch((err) => {
      console.error('Failed to load dashboard config', err);
      if (!active) return;
      setVsTeams([]);
      setMatchTypes([]);
      setMatchTypeCatalog([]);
      setSeasonTeamName('');
      setSeasonTeamLogo(teamLogo || '');
      setActivePlayers([]);
    });

    return () => {
      active = false;
    };
  }, [teamId, selectedSeason, teamLogo]);

  useEffect(() => {
    if (!teamId || !seasonDocId) {
      setPlayerEntries([]);
      return;
    }
    let active = true;
    setPlayerEntries([]);
    setLoadingPlayers(true);
    loadPlayerSeasonEntriesOnce(teamId, seasonDocId, async () => {
      const snapshot = await getDocs(collection(db, `teams/${teamId}/seasons/${seasonDocId}/database/playerStats/entries`));
      return snapshot.docs.map((entry) => {
        const row = entry.data();
        const modeText = normalizeFilterValue(row.Mode || row.ModeID || row.matchId);
        const mode = modeText.includes('SND') || modeText.includes('SEARCH') ? 'SND' :
          modeText.includes('CTL') || modeText.includes('CONTROL') ? 'CTL' :
          modeText.includes('HP') || modeText.includes('HARDPOINT') ? 'HP' : '';
        const resultText = normalizeFilterValue(row.Result || row.WinLose);
        return { ...row, id: entry.id, Mode: mode,
          Result: resultText === 'WIN' || resultText === 'W' ? 'Win' :
            resultText === 'LOSS' || resultText === 'LOSE' || resultText === 'L' ? 'Loss' : '' };
      });
    }).then((rows) => {
        if (!active) return;
        setPlayerEntries(rows);
      }).catch((err) => {
        console.error('Failed to load dashboard player entries', err);
        if (active) setPlayerEntries([]);
      }).finally(() => { if (active) setLoadingPlayers(false); });
    return () => { active = false; };
  }, [teamId, seasonDocId]);

  useEffect(() => {
    if (!teamId) return;
    let active = true;

    (async () => {
      setLoadingStats(true);
      const baseRef = seasonDocId
        ? collection(db, `teams/${teamId}/seasons/${seasonDocId}/database/teamStats/modes`)
        : collection(db, `teams/${teamId}/database/teamStats/modes`);

      const filters = [];
      if (startDate) filters.push(where('Date', '>=', startDate));
      if (endDate) filters.push(where('Date', '<=', endDate));
      if (region) filters.push(where('EnemyRegion', '==', region.toUpperCase()));
      if (enemyTeam) filters.push(where('EnemyTeam', '==', enemyTeam.toUpperCase()));

      const matchQuery = filters.length ? query(baseRef, ...filters) : query(baseRef);
      const snapshot = await getDocs(matchQuery);
      if (!active) return;
      const selectedCategoryKey = normalizeFilterValue(selectedMatchCategory);
      const selectedRegionKey = normalizeFilterValue(selectedMatchRegion);
      const selectedTypeSet = new Set(selectedMatchTypes.map(normalizeFilterValue));
      const catalogById = new Map(
        matchTypeCatalog.map((entry) => [String(entry.id || ''), entry]).filter(([id]) => id)
      );
      const catalogByLabel = new Map(
        matchTypeCatalog
          .map((entry) => [normalizeFilterValue(entry.label), entry])
          .filter(([label]) => label)
      );
      const rows = snapshot.docs
        .map((d) => d.data())
        .filter((row) => {
          const catalogEntry =
            catalogById.get(String(row.MatchTypeId || '')) ||
            catalogByLabel.get(normalizeFilterValue(row.MatchType));
          const rowCategory = normalizeFilterValue(row.MatchCategory || catalogEntry?.category);
          const rowRegion = normalizeFilterValue(row.MatchTypeRegion || catalogEntry?.region);
          const rowType = normalizeFilterValue(row.MatchType || catalogEntry?.label);

          if (selectedCategoryKey && rowCategory !== selectedCategoryKey) return false;
          if (selectedRegionKey && rowRegion !== selectedRegionKey) return false;
          if (selectedTypeSet.size && !selectedTypeSet.has(rowType)) return false;
          return true;
        });
      setAllStats(rows);
    })().catch((err) => {
      console.error('Failed to load dashboard stats', err);
      if (!active) return;
      setAllStats([]);
    }).finally(() => {
      if (active) setLoadingStats(false);
    });

    return () => {
      active = false;
    };
  }, [
    teamId,
    seasonDocId,
    startDate,
    endDate,
    region,
    enemyTeam,
    selectedMatchCategory,
    selectedMatchRegion,
    selectedMatchTypes,
    matchTypeCatalog,
  ]);

  const topTeamsByRegion = {
    GARENA: ['ELEVATE', 'STALWART', 'ABSOLUTE ZERO', 'ABC ESPORTS', 'THANATOS'],
    CHINA: ['Q9', 'XROCK', 'WOLVES', 'SPG', 'AG'],
    INDIA: ['GODLIKE', 'S8UL', 'XLR8'],
    EUROPE: ['ANIMUS', 'EXCLUSIVE', 'ACE GAMERS'],
    JAPAN: ['DIAVOLOS', 'XCEED'],
    LATAM: ['GALORYS', 'KAIZEN', 'AMIGOS', 'TLE ESPORTS'],
    NORTH_AMERICA: ['NA FALLOFFS']
  };

  const filteredPlayerEntries = useMemo(() => {
    const selectedCategoryKey = normalizeFilterValue(selectedMatchCategory);
    const selectedRegionKey = normalizeFilterValue(selectedMatchRegion);
    const selectedTypeSet = new Set(selectedMatchTypes.map(normalizeFilterValue));
    const catalogById = new Map(matchTypeCatalog.map((entry) => [String(entry.id || ''), entry]));
    const catalogByLabel = new Map(matchTypeCatalog.map((entry) => [normalizeFilterValue(entry.label), entry]));
    return playerEntries.filter((row) => {
      const catalogEntry = catalogById.get(String(row.MatchTypeId || '')) ||
        catalogByLabel.get(normalizeFilterValue(row.MatchType));
      if (startDate && (!row.Date || row.Date < startDate)) return false;
      if (endDate && (!row.Date || row.Date > endDate)) return false;
      if (region && normalizeFilterValue(row.Region || row.EnemyRegion) !== normalizeFilterValue(region)) return false;
      if (enemyTeam && normalizeFilterValue(row.VS || row.EnemyTeam) !== normalizeFilterValue(enemyTeam)) return false;
      if (selectedCategoryKey && normalizeFilterValue(row.MatchCategory || catalogEntry?.category) !== selectedCategoryKey) return false;
      if (selectedRegionKey && normalizeFilterValue(row.MatchTypeRegion || catalogEntry?.region) !== selectedRegionKey) return false;
      if (selectedTypeSet.size && !selectedTypeSet.has(normalizeFilterValue(row.MatchType || catalogEntry?.label))) return false;
      return true;
    });
  }, [playerEntries, startDate, endDate, region, enemyTeam, selectedMatchCategory, selectedMatchRegion, selectedMatchTypes, matchTypeCatalog]);

  const dashboard = useMemo(() => buildDashboardAnalytics({
    matches: allStats, playerRows: filteredPlayerEntries, activePlayers,
  }), [allStats, filteredPlayerEntries, activePlayers]);
  const closeEvidence = useCallback(() => setSelectedEvidence(null), []);
  useEffect(() => { setSelectedEvidence(null); }, [dashboard]);
  const openFlag = (flag, trigger) => { evidenceTrigger.current = trigger; setSelectedEvidence({ kind: 'flag', flag }); };
  const openMap = (mode, map, trigger) => { evidenceTrigger.current = trigger; setSelectedEvidence({ kind: 'map', mode, map }); };
  const filterScope = [SEASONS[selectedSeason]?.name || selectedSeason || 'All seasons',
    startDate || endDate ? `${startDate || 'Start'} to ${endDate || 'Today'}` : 'All dates',
    selectedMatchCategory || 'All categories', selectedMatchTypes.length ? selectedMatchTypes.join(', ') : 'All competitions',
    selectedMatchRegion || 'All competition regions', region || 'All opponent regions',
    enemyTeam ? `vs ${enemyTeam}` : 'All opponents'].join(' / ');

  const matchCategoryOptions = useMemo(() => {
    const availableCategories = new Set(
      matchTypeCatalog
        .filter((entry) => entry.active !== false)
        .map((entry) => normalizeFilterValue(entry.category))
    );
    return MATCH_TYPE_CATEGORIES.filter((option) => availableCategories.has(option.value));
  }, [matchTypeCatalog]);

  const matchRegionOptions = useMemo(
    () => getMatchTypeRegions(matchTypeCatalog, selectedMatchCategory),
    [matchTypeCatalog, selectedMatchCategory]
  );

  const matchTypeOptions = useMemo(() => {
    const uniqueTypes = new Map();
    getMatchTypeOptions(matchTypeCatalog, {
      category: selectedMatchCategory,
      region: selectedMatchRegion,
    }).forEach((entry) => {
      const label = String(entry.label || '').trim();
      const key = normalizeFilterValue(label);
      if (label && !uniqueTypes.has(key)) {
        uniqueTypes.set(key, label);
      }
    });
    if (!uniqueTypes.size) {
      (matchTypes || []).forEach((type) => {
        const label = String(type || '').trim();
        const key = normalizeFilterValue(label);
        if (label && !uniqueTypes.has(key)) {
          uniqueTypes.set(key, label);
        }
      });
    }
    return Array.from(uniqueTypes.values()).sort((a, b) => a.localeCompare(b));
  }, [matchTypeCatalog, matchTypes, selectedMatchCategory, selectedMatchRegion]);

  const selectedMatchTypeSet = useMemo(
    () => new Set(selectedMatchTypes.map(normalizeFilterValue)),
    [selectedMatchTypes]
  );

  const matchTypeDropdownLabel = selectedMatchTypes.length
    ? `${selectedMatchTypes.length} selected`
    : 'All';
  const filterItemClass = (isActive) => `dashboard-filter-item ${isActive ? 'is-active' : ''}`;

  const toggleMatchType = (type) => {
    const key = normalizeFilterValue(type);
    setSelectedMatchTypes((current) => {
      const currentSet = new Set(current.map(normalizeFilterValue));
      if (currentSet.has(key)) {
        return current.filter((item) => normalizeFilterValue(item) !== key);
      }
      return [...current, type];
    });
  };

  const handleMatchCategoryChange = (value) => {
    setSelectedMatchCategory(value);
    setSelectedMatchRegion('');
    setSelectedMatchTypes([]);
    setMatchTypeDropdownOpen(false);
  };

  const handleMatchRegionChange = (value) => {
    setSelectedMatchRegion(value);
    setSelectedMatchTypes([]);
    setMatchTypeDropdownOpen(false);
  };

  return (
    <div className="dashboard-overview v2-dashboard" aria-busy={loadingStats}>
      <header className="v2-header">
        {(seasonTeamLogo || teamLogo) && <img src={seasonTeamLogo || teamLogo} alt="" />}
        <div><h1>Team Dashboard</h1><p>{seasonTeamName || 'Team'} · {SEASONS[selectedSeason]?.name || selectedSeason || 'No season selected'}</p>
          <p className="v2-scope">{startDate || endDate ? `${startDate || 'Start'} to ${endDate || 'Today'}` : 'All dates'} ·
            {selectedMatchCategory || ' All categories'} · {selectedMatchTypes.length ? ` ${selectedMatchTypes.join(', ')}` : ' All competitions'} ·
            {selectedMatchRegion || ' All competition regions'} · {region || ' All opponent regions'}{enemyTeam ? ` · vs ${enemyTeam}` : ''}</p>
        </div>
      </header>

      {loadingStats ? <p className="v2-loading">Loading team results...</p> : <TeamFormCard data={dashboard.teamForm} />}

          <div className="filter-panel">
            <div className="aligned-left-block">
              <div className="dashboard-panel-heading">
                <h3>Filters</h3>
              </div>
              <div className="filter-grid">
                <SeasonFilter
                  selectedSeason={selectedSeason}
                  onSeasonChange={setSelectedSeason}
                  startDate={startDate}
                  onStartDateChange={setStartDate}
                  endDate={endDate}
                  onEndDateChange={setEndDate}
                />

                <div className={filterItemClass(!!selectedMatchCategory)}>
                  <label>Match Category</label>
                  <select
                    value={selectedMatchCategory}
                    onChange={(e) => handleMatchCategoryChange(e.target.value)}
                  >
                    <option value="">All</option>
                    {matchCategoryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={filterItemClass(!!selectedMatchRegion)}>
                  <label>Competition Region</label>
                  <select
                    value={selectedMatchRegion}
                    onChange={(e) => handleMatchRegionChange(e.target.value)}
                    disabled={!matchRegionOptions.length}
                  >
                    <option value="">All</option>
                    {matchRegionOptions.map((matchRegion) => (
                      <option key={matchRegion} value={matchRegion}>
                        {matchRegion}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={filterItemClass(selectedMatchTypes.length > 0)}>
                  <label>Competition</label>
                  <div className="dashboard-match-type-dropdown">
                    <button
                      type="button"
                      className="dashboard-match-type-trigger"
                      onClick={() => setMatchTypeDropdownOpen((open) => !open)}
                      aria-expanded={matchTypeDropdownOpen}
                    >
                      <span>{matchTypeDropdownLabel}</span>
                      <span aria-hidden="true">v</span>
                    </button>

                    {matchTypeDropdownOpen && (
                      <div className="dashboard-match-type-menu">
                        <div className="dashboard-match-type-actions">
                          <button
                            type="button"
                            onClick={() => setSelectedMatchTypes(matchTypeOptions)}
                            disabled={!matchTypeOptions.length}
                          >
                            Select all
                          </button>
                          <button type="button" onClick={() => setSelectedMatchTypes([])}>
                            Clear
                          </button>
                        </div>

                        <div className="dashboard-match-type-list">
                          {matchTypeOptions.length ? (
                            matchTypeOptions.map((type) => (
                              <label
                                key={type}
                                className={`dashboard-match-type-option ${selectedMatchTypeSet.has(normalizeFilterValue(type)) ? 'selected' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedMatchTypeSet.has(normalizeFilterValue(type))}
                                  onChange={() => toggleMatchType(type)}
                                />
                                <span>{type}</span>
                              </label>
                            ))
                          ) : (
                            <p>No match types configured</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className={filterItemClass(!!region)}>
                  <label>Opponent Region</label>
                  <select
                    value={region}
                    onChange={(e) => {
                      const selectedRegion = e.target.value;
                      setRegion(selectedRegion);
                      if (selectedRegion === '') {
                        setEnemyTeam('');
                      }
                    }}
                  >
                    <option value="">All</option>
                    {Object.keys(topTeamsByRegion).map((regionKey) => (
                      <option key={regionKey} value={regionKey}>{regionKey}</option>
                    ))}
                  </select>
                </div>

                <div className={filterItemClass(!!enemyTeam)}>
                  <label>Opponent</label>
                  <select
                    value={enemyTeam}
                    onChange={(e) => setEnemyTeam(e.target.value)}
                    disabled={!region}
                    required
                  >
                    <option value="">Select Opponent Team</option>
                    {vsTeams
                      .filter((team) => team.region === region)
                      .map((team, i) => (
                        <option key={i} value={team.name}>
                          {team.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

      {!loadingStats && <>
        <ModeHealth items={dashboard.modeHealth} />
        <div className="v2-middle"><CoachingFlags flags={dashboard.coachingFlags} onOpenEvidence={openFlag} /><RecentResults results={dashboard.recentResults} /></div>
        <MapPoolHealth groups={dashboard.mapPoolHealth} onOpenMap={openMap} />
        <PlayerForm players={dashboard.playerForm} loading={loadingPlayers}
          onViewPlayer={(player) => onViewPlayer?.({ ...player, season: selectedSeason })} />
      </>}
      {selectedEvidence && <DashboardEvidenceDrawer key={`${selectedEvidence.kind}-${selectedEvidence.flag?.title || selectedEvidence.map?.map}`}
        selection={selectedEvidence} scope={filterScope} onClose={closeEvidence} returnFocusTo={evidenceTrigger.current} />}
    </div>
  );
};

export default DashboardOverview;
